import { IResponse, consumer } from "@bemap/utils"
import { CourseCertificate } from "./course-certificates-service";

// Types based on the backend entities
export interface Course {
  id: number;
  title: string;
  description: string;
  instructor: string;
  category: 'partner' | 'analyst';
  level: 'beginner' | 'intermediate' | 'advanced';
  completed: boolean;
  progress: number;
  overallProgress: number;
  thumbnailUrl?: string;
  durationMinutes: number;
  price?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  modules?: Module[];
}

export interface Module {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  progress: number;
  order: number;
  durationMinutes: number;
  active: boolean;
  courseId: number;
  createdAt: string;
  updatedAt: string;
  contents?: Content[];
}

export interface Content {
  id: number;
  title: string;
  completed: boolean;
  active: boolean;
  duration: string;
  type: 'video' | 'document' | 'quiz';
  data: VideoData | DocumentData | QuizData | null;
  order: number;
  moduleId: number;
  createdAt: string;
  updatedAt: string;
  comments?: Comment[];
  questions?: Question[];
}

export interface VideoData {
  videoUrl: string;
  thumbnailUrl?: string;
  duration?: number;
}

export interface DocumentData {
  markdown: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  type: 'single' | 'multiple' | 'text';
  options?: string[];
  correctAnswer?: string | string[];
  explanation?: string;
}

export interface QuizData {
  title: string;
  description: string;
  questions: QuizQuestion[];
  passingScore: number;
  allowRetake: boolean;
  showCorrectAnswers: boolean;
}

export interface Comment {
  id: number;
  content: string;
  userId: number;
  contentId: number;
  parentId?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  replies?: Comment[];
}

export interface Question {
  id: number;
  content: string;
  userId: number;
  contentId: number;
  isAnswered: boolean;
  adminReply?: string;
  adminReplyAt?: string;
  adminReplyBy?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  adminUser?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface CourseStudent {
  id: number;
  courseId: number;
  userId: number;
  status: 'enrolled' | 'in_progress' | 'completed' | 'dropped' | 'suspended';
  certificateId?: number;
  certificate?: CourseCertificate;
  overallProgress: number;
  completedModules: number;
  totalModules: number;
  completedContents: number;
  totalContents: number;
  enrolledAt?: string;
  startedAt?: string;
  completedAt?: string;
  lastAccessedAt?: string;
  moduleProgress?: Record<number, {
    completed: boolean;
    progress: number;
    completedContents: number;
    totalContents: number;
    lastAccessedAt: string;
  }>;
  contentProgress?: Record<number, {
    completed: boolean;
    lastAccessedAt: string;
    timeSpent: number;
  }>;
  totalTimeSpent: number;
  grade?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  course?: Course;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

// Course API calls
export const getCourses = async (): Promise<IResponse> => {
  const url = `/university/courses`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCourseById = async (id: number): Promise<IResponse> => {
  const url = `/university/courses/${id}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Content API calls
export const getContentById = async (id: number): Promise<IResponse> => {
  const url = `/university/content/${id}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getContentComments = async (contentId: number): Promise<IResponse> => {
  const url = `/university/content/${contentId}/comments`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getContentQuestions = async (contentId: number): Promise<IResponse> => {
  const url = `/university/content/${contentId}/questions`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Comment API calls
export const createComment = async (payload: Partial<Comment>): Promise<IResponse> => {
  const url = `/university/comments`;

  try {
    return await consumer('POST', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Question API calls
export const createQuestion = async (payload: Partial<Question>): Promise<IResponse> => {
  const url = `/university/questions`;

  try {
    return await consumer('POST', url, payload);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Student enrolled courses API calls
export const getEnrolledCourses = async (userId: number): Promise<IResponse> => {
  const url = `/university/students/${userId}/courses`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Course enrollment API calls
export const enrollInCourse = async (courseId: number, userId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/enroll/${userId}`;

  try {
    return await consumer('POST', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const unenrollFromCourse = async (courseId: number, userId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${userId}`;

  try {
    return await consumer('DELETE', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getMyCourses = async (): Promise<IResponse> => {
  const url = `/university/courses/my-courses`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Completion API calls
export const markContentCompleted = async (
  courseStudentId: number,
  contentId: number,
  moduleId: number,
  contentType: 'video' | 'document' | 'quiz',
  score?: number,
  quizAnswers?: Record<string, any>,
  timeSpent?: number
): Promise<IResponse> => {
  const url = `/university/content/${contentId}/completions`;

  try {
    return await consumer('POST', url, {
      courseStudentId,
      contentId,
      moduleId,
      contentType,
      score,
      quizAnswers,
      timeSpent
    });
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const updateContentProgress = async (
  courseStudentId: number,
  contentId: number,
  timeSpent: number
): Promise<IResponse> => {
  const url = `/university/content/${contentId}/completions/progress`;

  try {
    return await consumer('PUT', url, {
      courseStudentId,
      timeSpent
    });
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const updateQuizScore = async (
  courseStudentId: number,
  contentId: number,
  answers: Record<string, any>
): Promise<IResponse> => {
  const url = `/university/content/${contentId}/completions/quiz`;

  try {
    return await consumer('PUT', url, {
      courseStudentId,
      answers
    });
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getContentCompletion = async (
  courseStudentId: number,
  contentId: number
): Promise<IResponse> => {
  const url = `/university/content/${contentId}/completions?courseStudentId=${courseStudentId}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getModuleCompletion = async (
  courseStudentId: number,
  moduleId: number
): Promise<IResponse> => {
  const url = `/university/modules/${moduleId}/completions?courseStudentId=${courseStudentId}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const getCourseCompletions = async (
  courseId: number,
  studentId: number
): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${studentId}/completions`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Get course completions by courseId (for current user)
export const getCourseCompletionsByCourseId = async (courseId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/completions`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Get course analytics by courseId (for current user)
export const getCourseAnalyticsByCourseId = async (courseId: number): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/analytics`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Update last viewed content
export const updateLastViewedContent = async (
  courseId: number,
  userId: number,
  contentId: number
): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${userId}/last-viewed-content`;

  try {
    return await consumer('PUT', url, { contentId });
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Content type specific completion endpoints
export const markVideoCompleted = async (
  courseStudentId: number,
  contentId: number,
  moduleId: number,
  timeSpent?: number
): Promise<IResponse> => {
  const url = `/university/content/${contentId}/completions/video`;

  try {
    return await consumer('POST', url, {
      courseStudentId,
      moduleId,
      timeSpent
    });
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const markDocumentCompleted = async (
  courseStudentId: number,
  contentId: number,
  moduleId: number,
  timeSpent?: number
): Promise<IResponse> => {
  const url = `/university/content/${contentId}/completions/document`;

  try {
    return await consumer('POST', url, {
      courseStudentId,
      moduleId,
      timeSpent
    });
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

export const markQuizCompleted = async (
  courseStudentId: number,
  contentId: number,
  moduleId: number,
  score: number,
  answers: Record<string, any>,
  timeSpent?: number
): Promise<IResponse> => {
  const url = `/university/content/${contentId}/completions/quiz`;

  try {
    return await consumer('POST', url, {
      courseStudentId,
      moduleId,
      score,
      answers,
      timeSpent
    });
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Module completion endpoints
export const initializeModuleCompletion = async (
  courseStudentId: number,
  moduleId: number,
  totalContents: number
): Promise<IResponse> => {
  const url = `/university/modules/${moduleId}/completions/initialize`;

  try {
    return await consumer('POST', url, {
      courseStudentId,
      totalContents
    });
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Analytics endpoint
export const getCourseAnalytics = async (
  courseId: number,
  studentId: number
): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${studentId}/analytics`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
};

// Get course enrollment ID for a specific course and user
export const getCourseEnrollmentId = async (
  courseId: number,
  userId: number
): Promise<IResponse> => {
  const url = `/university/courses/${courseId}/students/${userId}`;

  try {
    return await consumer('GET', url);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'An error occurred';
    throw {
      statusCode: error.response?.status || 500,
      message: msg
    };
  }
}; 